import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import SettingStore from "stores/settings";
import * as CONSTANTS from "../../constants";
import { useMyLearning } from "../../context";
import { getTag, getCategoryID, getTagValue } from "../../utils";

const FilterContext = createContext();

export const useFilter = () => useContext(FilterContext);

export const FilterProvider = ({ children, mode }) => {
  const {
    filter: contextFilter,
    fetchMyLearningData,
    applyFilter,
    setStatus,
    setSortBy: setContextSortBy,
    courses,
    programs,
    filteredCourses,
    filteredPrograms,
    searching,
    categories,
    tags,
  } = useMyLearning();

  const [state, setState] = useState({
    filter: {
      ...contextFilter,
      drawerOpen: false,
    },
    hitCount: 0,
    totalCount: 0,
  });

  const updateState = useCallback((updates) => {
    if (typeof updates === "function") {
      setState((prevState) => ({
        ...prevState,
        ...updates(prevState),
      }));
    } else {
      setState((prevState) => ({
        ...prevState,
        ...updates,
      }));
    }
  }, []);

  useEffect(() => {
    updateState({
      totalCount: (courses?.length ?? 0) + (programs?.length ?? 0),
    });
    filterByQueryString();
  }, []);

  // Sync with contextFilter changes
  useEffect(() => {
    updateState((prevState) => ({
      filter: {
        ...prevState.filter,
        ...contextFilter,
      },
    }));
  }, [contextFilter]);

  useEffect(() => {
    if (!searching) {
      updateState({
        hitCount:
          (filteredCourses.active?.length ?? 0) +
          (filteredPrograms.active?.length ?? 0),
      });
    }
  }, [filteredCourses, filteredPrograms, searching]);

  // sortBy/status persistence handled centrally in provider

  // Removed effect-driven propagation. All updates call applyFilter explicitly.

  // Removed unused helpers (hasStateChanged, hasSelectedTagsChanged, updateFilter)

  const filterByQueryString = () => {
    const tagString = SettingStore.getSettingValue("mycoursesfiltertags");
    if (!tagString) return;

    const tagsList = tagString.split(",");
    const queryParams = new URLSearchParams(window.location.hash.split("?")[1]);

    tagsList.forEach((tag) => {
      const tagValue = queryParams.get(tag);
      if (!tagValue) return;

      if (tag === "category") {
        const categoryId = getCategoryID(tagValue, categories);
        updateState((prevState) => ({
          ...prevState,
          filter: { ...prevState.filter, categoryId },
        }));
      } else {
        const tagData = getTag(tag, tags);
        const valueId = getTagValue(tagData, tagValue);
        setTagFilter(0, valueId);
      }
    });
  };

  const setFilterStatus = useCallback(
    (updatedStatus) => {
      localStorage.setItem(CONSTANTS.STORAGE_KEY_FILTER_STATUS, updatedStatus);

      setStatus(updatedStatus);

      updateState((prevState) => ({
        filter: { ...prevState.filter, status: updatedStatus },
      }));
    },
    [state.filter, setStatus],
  );

  const setSortBy = useCallback(
    (sortBy) => {
      updateState((prevState) => ({
        filter: { ...prevState.filter, sortBy },
      }));
      setContextSortBy(sortBy);
    },
    [state.filter, setContextSortBy],
  );

  const setSearchText = useCallback((searchText) => {
    updateState((prevState) => ({
      filter: { ...prevState.filter, searchText },
    }));
  }, []);

  const onSearch = useCallback(
    (searchText = state.filter.searchText) => {
      const fetchFiltredContent = true;
      fetchMyLearningData(
        {
          searchText,
          selectedTags: state.filter.selectedTags,
          sortBy: state.filter.sortBy,
          mode,
          filter: state.filter.filter,
          categoryId: state.filter.categoryId,
          displayAdminCourses: state.filter.displayAdminCourses,
        },
        fetchFiltredContent,
      );
    },
    [state.filter, fetchMyLearningData, mode],
  );

  const setTagFilter = useCallback(
    (no, value) => {
      // Update local state immediately for UI responsiveness
      updateState((prevState) => ({
        filter: {
          ...prevState.filter,
          selectedTags: [
            ...prevState.filter.selectedTags.slice(0, no),
            value,
            ...prevState.filter.selectedTags.slice(no + 1),
          ],
        },
      }));

      // Build updated filter for server-side fetch
      const updatedSelectedTags = [
        ...state.filter.selectedTags.slice(0, no),
        value,
        ...state.filter.selectedTags.slice(no + 1),
      ];

      const fetchFiltredContent = true;
      fetchMyLearningData(
        {
          searchText: state.filter.searchText,
          selectedTags: updatedSelectedTags,
          sortBy: state.filter.sortBy,
          mode,
          filter: state.filter.filter,
          categoryId: state.filter.categoryId,
          displayAdminCourses: state.filter.displayAdminCourses,
        },
        fetchFiltredContent,
      );
    },
    [fetchMyLearningData, state.filter, mode],
  );

  const setCategoryId = useCallback(
    (categoryId) => {
      updateState((prevState) => ({
        filter: { ...prevState.filter, categoryId },
      }));
      applyFilter({
        ...contextFilter,
        selectedTags: state.filter.selectedTags,
        categoryId,
        searchText: state.filter.searchText,
        sortBy: state.filter.sortBy,
        displayAdminCourses: state.filter.displayAdminCourses,
        status: state.filter.status,
        mode,
      });
    },
    [applyFilter, contextFilter, state.filter, mode],
  );

  const setDisplayAdminCourses = useCallback(
    (value) => {
      localStorage.setItem(CONSTANTS.STORAGE_KEY_ADMIN_TOGGLE, value);
      updateState((prevState) => ({
        filter: {
          ...prevState.filter,
          displayAdminCourses: value,
        },
      }));

      const hasActiveTags =
        Array.isArray(state.filter.selectedTags) &&
        state.filter.selectedTags.some((t) => t !== 0);

      const hasActiveSearch =
        state.filter.searchText && state.filter.searchText.length >= 2;

      if (hasActiveTags || hasActiveSearch) {
        const fetchFiltredContent = true;
        fetchMyLearningData(
          {
            searchText: state.filter.searchText,
            selectedTags: state.filter.selectedTags,
            sortBy: state.filter.sortBy,
            mode,
            filter: state.filter.filter,
            categoryId: state.filter.categoryId,
            displayAdminCourses: value,
            status: state.filter.status,
          },
          fetchFiltredContent,
        );
      } else {
        applyFilter({
          ...contextFilter,
          selectedTags: state.filter.selectedTags,
          categoryId: state.filter.categoryId,
          searchText: state.filter.searchText,
          sortBy: state.filter.sortBy,
          displayAdminCourses: value,
          status: state.filter.status,
          mode,
        });
      }
    },
    [applyFilter, fetchMyLearningData, contextFilter, state.filter, mode],
  );

  useEffect(() => {
    const storedAdminToggle = localStorage.getItem(
      CONSTANTS.STORAGE_KEY_ADMIN_TOGGLE,
    );
    if (storedAdminToggle === null) {
      return;
    }
    const storedValue = storedAdminToggle === "true";
    if (storedValue !== state.filter.displayAdminCourses) {
      setDisplayAdminCourses(storedValue);
    }
  }, [setDisplayAdminCourses, state.filter.displayAdminCourses]);

  const toggleDrawer = useCallback(() => {
    updateState((prevState) => ({
      filter: {
        ...prevState.filter,
        drawerOpen: !prevState.filter.drawerOpen,
      },
    }));
  }, []);

  const closeDrawer = useCallback(() => {
    updateState((prevState) => ({
      filter: {
        ...prevState.filter,
        drawerOpen: false,
      },
    }));
  }, []);

  const handleClearFilter = useCallback(() => {
    const currentLength = state.filter.selectedTags.length;
    const resetSelectedTags = new Array(currentLength).fill(0);

    const resetFilter = {
      searchText: "",
      selectedTags: resetSelectedTags,
      categoryId: 0,
      sortBy: state.filter.sortBy,
      mode,
      status: CONSTANTS.PROGRESS_ALL,
      displayAdminCourses: false,
    };

    // Update local state first
    updateState((prevState) => ({
      filter: {
        ...prevState.filter,
        ...resetFilter,
      },
    }));

    // Re-fetch full, unfiltered data from server to undo prior server-side tag filtering
    const fetchFilteredContent = false;
    fetchMyLearningData(
      {
        ...resetFilter,
        selectedTags: resetSelectedTags.map((v) => Number(v)),
      },
      fetchFilteredContent,
    );

    // Persist status explicitly to avoid race with provider setStatus
    localStorage.setItem(
      CONSTANTS.STORAGE_KEY_FILTER_STATUS,
      CONSTANTS.PROGRESS_ALL,
    );
    localStorage.setItem(CONSTANTS.STORAGE_KEY_ADMIN_TOGGLE, "false");
  }, [fetchMyLearningData, state.filter.sortBy, mode]);

  const value = {
    state: state.filter,
    setState: (updates) => {
      if (typeof updates === "function") {
        updateState((prevState) => ({
          filter: updates(prevState.filter),
        }));
      } else {
        updateState({ filter: updates });
      }
    },
    hitCount: state.hitCount,
    totaltCount: state.totalCount,
    showHitCount: state.totalCount !== state.hitCount,
    setFilterStatus,
    setSortBy,
    setSearchText,
    onSearch,
    setTagFilter,
    setCategoryId,
    setDisplayAdminCourses,
    toggleDrawer,
    closeDrawer,
    handleClearFilter,
    mode,
  };

  return (
    <FilterContext.Provider value={value}>{children}</FilterContext.Provider>
  );
};

export default FilterProvider;
